import { t } from '@unistocks/localization';
import { ApplicationSettingConfigurationDto } from '@unistocks/proxy/axios-client';
import { useApplicationConfiguration } from '@unistocks/shared/hooks/use-application-configuration';
import { AbpSettings } from '@unistocks/shared/settings';
import { KeyValue } from '@unistocks/types/key-value';
import { z } from 'zod';

export interface GeneratePasswordRequirementsOptions {
  withConfirmField?: boolean;
  passwordFieldName?: string;
  confirmPasswordFieldName?: string;
}

export type PasswordRule = 'small' | 'capital' | 'number' | 'special' | 'RequiredLength';
type ValidationMessages = Array<KeyValue<PasswordRule, string | number | undefined>>;

const regexMap: Partial<Record<PasswordRule, RegExp>> = {
  small: /.*[a-z].*/,
  capital: /.*[A-Z].*/,
  number: /.*[0-9].*/,
  special: /.*[^0-9a-zA-Z].*/
};

export class PasswordValidator {
  constructor(private readonly settings: ApplicationSettingConfigurationDto['values']) {}

  public generatePasswordValidationSchema = (params: GeneratePasswordRequirementsOptions = {}) => {
    const {
      withConfirmField = true,
      passwordFieldName = 'password',
      confirmPasswordFieldName = 'confirmPassword'
    } = params;

    const shape: Record<string, z.ZodType> = {
      [passwordFieldName]: this.generatePasswordRequirements(),
      [confirmPasswordFieldName]: z.string().nullish()
    };

    return z.object(shape).superRefine((values, ctx) => {
      if (!withConfirmField) return;
      if (values[confirmPasswordFieldName] === values[passwordFieldName]) return;

      ctx.addIssue({
        code: 'custom',
        path: [confirmPasswordFieldName],
        message: t('AbpIdentity::Volo.Abp.Identity:PasswordConfirmationFailed')
      });
    });
  };

  public generatePasswordRequirements = () => {
    const rules = this.getPasswordValidators();
    const requiredMessage = t('::ThisFieldIsRequired.');

    let passwordRequirements = z
      .string(requiredMessage)
      .min(1, requiredMessage);

    for (const rule of rules) {
      switch (rule.key) {
        case 'small':
        case 'capital':
        case 'number':
        case 'special':
          passwordRequirements = passwordRequirements.regex(regexMap[rule.key]!, {
            error: this.getPasswordRuleText(rule)
          });
          break;

        case 'RequiredLength':
          passwordRequirements = passwordRequirements.min(
            rule.value as number,
            this.getPasswordRuleText(rule)
          );
          break;
      }
    }

    return passwordRequirements;
  };

  public getPasswordValidators(): ValidationMessages {
    const getRule = this.getRuleFn();

    const passwordRulesArr: ValidationMessages = [];

    if (getRule('RequireDigit') === 'true') {
      passwordRulesArr.push({ key: 'number', value: undefined });
    }

    if (getRule('RequireLowercase') === 'true') {
      passwordRulesArr.push({ key: 'small', value: undefined });
    }

    if (getRule('RequireUppercase') === 'true') {
      passwordRulesArr.push({ key: 'capital', value: undefined });
    }

    if (getRule('RequireNonAlphanumeric') === 'true') {
      passwordRulesArr.push({ key: 'special', value: undefined });
    }

    if (Number.isInteger(+getRule('RequiredLength'))) {
      passwordRulesArr.push({ key: 'RequiredLength', value: +getRule('RequiredLength') });
    }

    return passwordRulesArr;
  }

  private getRuleFn() {
    const passwordRules = Object.entries<string>(this.settings).filter(([key, value]) =>
      key.startsWith(AbpSettings.Password)
    );

    return (settingKey: string) => {
      return passwordRules.find(([key]) => key.includes(settingKey))?.[1]?.toLowerCase() ?? '';
    };
  }

  public getPasswordRuleText(rule: any): string {
    switch (rule.key) {
      case 'small':
        return t('AbpIdentity::Volo.Abp.Identity:PasswordRequiresLower');

      case 'capital':
        return t('AbpIdentity::Volo.Abp.Identity:PasswordRequiresUpper');

      case 'number':
        return t('AbpIdentity::Volo.Abp.Identity:PasswordRequiresDigit');

      case 'special':
        return t('AbpIdentity::Volo.Abp.Identity:PasswordRequiresNonAlphanumeric');

      case 'RequiredLength':
        return t('AbpIdentity::Volo.Abp.Identity:PasswordTooShort', {
          0: this.settings['Abp.Identity.Password.RequiredLength']
        });

      default:
        return '';
    }
  }
}

export const usePasswordValidationSchema = (params: GeneratePasswordRequirementsOptions = {}) =>
  useApplicationConfiguration({
    select: config => {
      if (config?.setting?.values) {
        return new PasswordValidator(config.setting.values).generatePasswordValidationSchema(
          params
        );
      }

      return z.object({});
    }
  });
